import { useState } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/primitives";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROLES = ["Senior Architect", "MEP Engineer", "Project Manager · Design & Build", "Interior Designer", "Sub Contractor", "General Application"];

export default function CareersPage() {
  const [form, setForm] = useState({ name: "", email: "", role: ROLES[0] });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!file) {
      toast.error("Please attach your resume (PDF or Word, under 10 MB).");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("role", form.role);
      data.append("resume", file);
      const res = await fetch(`${BACKEND_URL}/api/careers`, { method: "POST", body: data });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Request failed");
      toast.success("Application received; we'll be in touch soon.");
      setForm({ name: "", email: "", role: ROLES[0] });
      setFile(null);
      document.getElementById("resume-input").value = "";
    } catch (err) {
      toast.error(err.message === "Request failed" ? "Something went wrong. Please try again." : err.message);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border-b border-ink/25 bg-transparent py-3.5 text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/40 focus:border-maroon";

  return (
    <main className="bg-paper" data-testid="careers-page">
      <PageHeader
        index="C"
        eyebrow="Join Us"
        title="Careers at ARA Turnkey"
        sub="We're always looking for people who believe spaces should do more than look good; people who think about how a room feels at 9 am and how it performs at 6 pm."
      />

      <section className="px-6 py-20 lg:px-12 lg:py-28" data-testid="careers-apply-section">
        <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl leading-tight text-ink lg:text-4xl">
                Share your <em className="italic text-maroon">resume</em>
              </h2>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-ink/65">
                Tell us who you are and what you're great at. Whether or not a role is open, we keep every
                application on file; projects move fast and so do we.
              </p>
              <p className="mt-8 text-sm text-ink/60">
                Prefer email? Send it to{" "}
                <a
                  href="mailto:careers@araturnkey.com"
                  data-testid="careers-email-link"
                  className="border-b border-ink/30 pb-0.5 text-ink transition-colors duration-300 hover:border-maroon hover:text-maroon"
                >
                  careers@araturnkey.com
                </a>
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form onSubmit={submit} className="grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2" data-testid="careers-form">
                <div>
                  <label htmlFor="cr-name" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                    Name *
                  </label>
                  <input id="cr-name" required value={form.name} onChange={set("name")} className={field} placeholder="Your full name" data-testid="careers-input-name" />
                </div>
                <div>
                  <label htmlFor="cr-email" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                    Email *
                  </label>
                  <input id="cr-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="you@email.com" data-testid="careers-input-email" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cr-role" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                    Role
                  </label>
                  <select id="cr-role" value={form.role} onChange={set("role")} className={field} data-testid="careers-select-role">
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="resume-input" className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">
                    Resume (PDF or Word, max 10 MB) *
                  </label>
                  <label
                    htmlFor="resume-input"
                    data-testid="careers-file-dropzone"
                    className="mt-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-ink/30 px-6 py-10 text-center transition-colors duration-300 hover:border-maroon"
                  >
                    <span className="font-serif text-lg text-ink">{file ? file.name : "Attach your resume"}</span>
                    <span className="text-xs text-ink/50">{file ? "Click to replace" : "PDF or Word document"}</span>
                  </label>
                  <input
                    id="resume-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="careers-input-resume"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="careers-submit-button"
                    className="rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:scale-[1.03] disabled:opacity-60"
                  >
                    {sending ? "Uploading…" : "Submit Application"}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
